import { useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Globe, Package, Factory } from "lucide-react";

export const WorldMapDetailed = ({ countries }) => {
  const [hoveredCountry, setHoveredCountry] = useState(null);

  // Convert lat/lng to SVG coordinates
  const projectToSVG = (lat, lng) => {
    const x = ((lng + 180) / 360) * 1200;
    const y = ((90 - lat) / 180) * 600;
    return { x, y };
  };

  return (
    <div className="relative w-full">
      {/* Main World Map Container */}
      <div className="relative bg-gradient-to-br from-slate-800 via-slate-900 to-slate-950 rounded-3xl p-8 border-2 border-amber-500/30 shadow-2xl overflow-hidden">
        
        {/* Sparkle Stars Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-amber-300 rounded-full animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                opacity: Math.random() * 0.7 + 0.3
              }}
            />
          ))}
        </div>

        {/* Legend - moved to top of container, not absolute */}
        <div className="mb-4 flex justify-end">
          <div className="bg-slate-900/90 backdrop-blur-sm rounded-xl px-4 py-3 border border-amber-500/30 shadow-xl inline-flex items-center gap-3">
            <div className="w-4 h-4 bg-gradient-to-b from-amber-400 to-amber-600 rounded-full shadow-lg"></div>
            <span className="font-semibold text-amber-100 text-sm">Our Global Presence</span>
            <Globe className="w-4 h-4 text-amber-300" />
            <span className="text-xs text-amber-200/80">{countries.length} Countries</span>
          </div>
        </div>

        <svg
          viewBox="0 0 1200 600"
          className="w-full h-auto relative z-10"
          style={{ minHeight: "500px" }}
        >
          <defs>
            {/* Gradients */}
            <linearGradient id="continentGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#FCD34D" stopOpacity="0.4" />
              <stop offset="50%" stopColor="#F59E0B" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#D97706" stopOpacity="0.3" />
            </linearGradient>
            
            <linearGradient id="pinGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#FCD34D" />
              <stop offset="100%" stopColor="#F59E0B" />
            </linearGradient>

            <filter id="glow">
              <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>

          {/* Real World Map Background Image */}
          <image 
            href="https://static.prod-images.emergentagent.com/jobs/f92a4a12-feda-4522-b7a4-7fe7961897f7/images/6d041c6e093c1722b146200f210038f3c15c07eb9685862cf2ea54682782d05c.png"
            x="0" 
            y="0" 
            width="1200" 
            height="600"
            preserveAspectRatio="xMidYMid slice"
            opacity="0.95"
          />

          {/* Location Pins */}
          {countries.map((country, index) => {
            const { x, y } = projectToSVG(country.lat, country.lng);
            const isHovered = hoveredCountry === country.name;

            return (
              <g
                key={index}
                onMouseEnter={() => setHoveredCountry(country.name)}
                onMouseLeave={() => setHoveredCountry(null)}
                className="cursor-pointer transition-all duration-300"
                style={{ transformOrigin: `${x}px ${y}px` }}
              >
                {/* Pulsing Circle Background */}
                <circle
                  cx={x}
                  cy={y}
                  r={isHovered ? "12" : "8"}
                  fill="#F59E0B"
                  opacity="0.3"
                  className="transition-all duration-300"
                >
                  <animate
                    attributeName="r"
                    values={isHovered ? "12;16;12" : "8;12;8"}
                    dur="2s"
                    repeatCount="indefinite"
                  />
                </circle>

                {/* Main Pin */}
                <g transform={`translate(${x}, ${y})`}>
                  {/* Pin Shadow */}
                  <ellipse
                    cx="0"
                    cy="18"
                    rx={isHovered ? "5" : "3.5"}
                    ry={isHovered ? "2" : "1.5"}
                    fill="rgba(0,0,0,0.3)"
                    className="transition-all duration-300"
                  />
                  
                  {/* Pin Shape */}
                  <path
                    d="M 0,-16 C -5,-16 -9,-12 -9,-7 C -9,-3 0,0 0,0 C 0,0 9,-3 9,-7 C 9,-12 5,-16 0,-16 Z"
                    fill="url(#pinGradient)"
                    stroke="#D97706"
                    strokeWidth="1.5"
                    filter={isHovered ? "url(#glow)" : "none"}
                    className="transition-all duration-300"
                    style={{
                      transform: isHovered ? "scale(1.3)" : "scale(1)"
                    }}
                  />
                  
                  {/* Pin Center Dot */}
                  <circle
                    cx="0"
                    cy="-10"
                    r={isHovered ? "3" : "2.5"}
                    fill="white"
                    className="transition-all duration-300"
                  />
                </g>

                {/* Hover Tooltip */}
                {isHovered && (
                  <g transform={`translate(${x}, ${y - 30})`}>
                    <rect
                      x="-60"
                      y="-25"
                      width="120"
                      height="35"
                      rx="6"
                      fill="rgba(15, 15, 15, 0.95)"
                      stroke="#F59E0B"
                      strokeWidth="2"
                    />
                    <text
                      x="0"
                      y="-2"
                      textAnchor="middle"
                      fill="white"
                      fontSize="13"
                      fontWeight="600"
                    >
                      {country.name}
                    </text>
                  </g>
                )}
              </g>
            );
          })}

          {/* Decorative Stars */}
          {[...Array(15)].map((_, i) => (
            <g key={`star-${i}`}>
              <circle
                cx={100 + i * 70}
                cy={50 + (i % 3) * 150}
                r="2"
                fill="#FCD34D"
                opacity="0.6"
              >
                <animate
                  attributeName="opacity"
                  values="0.3;0.8;0.3"
                  dur={`${2 + Math.random() * 2}s`}
                  repeatCount="indefinite"
                />
              </circle>
            </g>
          ))}
        </svg>
      </div>

      {/* Info Cards - Below the map instead of absolute */}
      <div className="grid grid-cols-2 gap-4 mt-6">
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-xl p-6 shadow-xl">
          <div className="flex items-center gap-4 text-white">
            <Factory className="w-10 h-10" />
            <div>
              <div className="text-sm opacity-90">Manufacturing Hub</div>
              <div className="font-bold text-xl">Chennai, India</div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-amber-600 to-amber-700 rounded-xl p-6 shadow-xl">
          <div className="flex items-center gap-4 text-white">
            <Package className="w-10 h-10" />
            <div>
              <div className="text-sm opacity-90">Global Delivery</div>
              <div className="font-bold text-xl">5000+ Machines</div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4 mt-8">
        <Card className="border-2 border-amber-200 bg-gradient-to-br from-white to-amber-50 hover:shadow-xl transition-shadow">
          <CardContent className="p-6 text-center">
            <div className="text-4xl font-bold bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
              {countries.length}+
            </div>
            <div className="text-sm text-slate-600 mt-2 font-medium">Countries Served</div>
          </CardContent>
        </Card>
        <Card className="border-2 border-amber-200 bg-gradient-to-br from-white to-amber-50 hover:shadow-xl transition-shadow">
          <CardContent className="p-6 text-center">
            <div className="text-4xl font-bold bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
              800+
            </div>
            <div className="text-sm text-slate-600 mt-2 font-medium">Happy Clients</div>
          </CardContent>
        </Card>
        <Card className="border-2 border-amber-200 bg-gradient-to-br from-white to-amber-50 hover:shadow-xl transition-shadow">
          <CardContent className="p-6 text-center">
            <div className="text-4xl font-bold bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
              5000+
            </div>
            <div className="text-sm text-slate-600 mt-2 font-medium">Machines Delivered</div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
